import { cache } from '@/container/apollo-client/cache';
import {
  authMeQuery,
  AuthMeResponse,
  CallerId,
  ChatMessage,
  Channel,
  Contact,
  ContactByIdQuery,
  ContactNoteV2,
  CountPinnedChatMessagesQuery,
  CountPinnedMessageConversationsQuery,
  Country,
  DevicePlatformEnum,
  GetAutoRechargeConfigsQuery,
  GetChatMessagesV2Query,
  GetLowBalanceAlertQuery,
  InvoiceStatusEnum,
  MessageConversation,
  workspaceAuthMeV2Document,
  workspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { ApolloCache, DocumentNode, gql, Reference } from '@apollo/client';
import { CURSOR_CONTACT_NOTES, CURSOR_MEMBER_MESSAGES, CURSOR_MESSAGES } from '../cursor.graphql';
import {
  GET_CONTACT_BY_ID,
  QUERY_GET_AUTO_RECHARGE_CONFIG,
  QUERY_GET_LOW_BALANCE_ALERT,
  QUERY_GET_WALLET_DETAILS,
  QUERY_MEMBER_PIN_MESSAGE_COUNT,
  QUERY_PIN_MESSAGE_COUNT,
  RECENT_MESSAGE_CONVERSATION,
  AUTH_ME,
} from '../query.graphql';
import { createRecentMessageOnSendMessage, createSurrogateMessage } from './cache-create-objects';

export function cacheMutateAllTypenames(
  fragment: DocumentNode,
  typenameKey: string,
  dataFn: (cacheId: string) => Record<PropertyKey, string | number | boolean>
) {
  const cacheObjects = Object.keys(cache.extract()).filter((key) => key.startsWith(typenameKey));

  cacheObjects.forEach((cacheId) => {
    cache.writeFragment({
      id: cacheId,
      fragment,
      data: dataFn(cacheId.split(':')[1]),
    });
  });
}

export function cacheRemoveConversationContact(channelId: number, conversationId: number) {
  const QUERY = {
    query: RECENT_MESSAGE_CONVERSATION,
    variables: {
      channelId,
    },
  };

  try {
    cache.updateQuery(QUERY, (data) => {
      const filteredEdges = data?.recentMessageConversations?.edges?.filter((edge: { node: { id: number } }) => {
        return conversationId !== edge?.node?.id;
      });

      const updatedData = {
        ...data?.recentMessageConversations,
        edges: filteredEdges,
      };

      return {
        ...data,
        recentMessageConversations: updatedData,
      };
    });
  } catch {
    console.error('There was some issue on cacheRemoveConversationContact');
  }
}

const NEW_MESSAGE_FRAGMENT = gql`
  fragment NewMessage on MessageConversation {
    id
    messageContent
    markAsClosed
    direction
    durationConference {
      endDate
      startDate
    }
    client {
      id
      name
      phoneNumber
      country {
        id
        flag
      }
      profilePicture {
        id
        url
      }
    }
    agent {
      callPlatform
      user {
        id
        name
        profilePicture {
          id
          url
        }
      }
      userOnlineManagement {
        isActive
      }
    }
    pinned
    conversationStatus
    conversationType
    recordingUrl
    numMedia
    friendlyName
    voicemailRecordingUrl
    createdAt
    forwardedAt
    forwardedTo
    markAsBlocked
    messageConversationErrorLog {
      message
      code
      statusCode
    }
    callRecording {
      id
      url
      transcription {
        id
        jobStatus
      }
    }
    voicemailRecording {
      id
      url
      transcription {
        id
        jobStatus
      }
    }
  }
`;

export function cacheAddSurrogateMessage({
  channelId,
  clientId,
  messageContent = '',
  agent,
  numMedia,
}: {
  channelId: string;
  clientId: string;
  messageContent: string;
  agent: AuthMeResponse;
  numMedia: number;
}) {
  try {
    const client = cache.readQuery({
      query: GET_CONTACT_BY_ID,
      variables: { contactId: Number(clientId) },
    }) as ContactByIdQuery;

    let cacheReferenceId = '';

    const surrogateMessage = createSurrogateMessage({
      clientId,
      channelId,
      messageContent,
      numMedia,
      agent: {
        id: agent?.data?.user?.id || 0,
        callPlatform: DevicePlatformEnum.web,
        name: agent?.data?.user?.name + '',
        profilePicture: {
          id: agent?.data?.user?.profilePicture?.id ?? 0,
          url: agent?.data?.user?.profilePicture?.url ?? '',
        },
      },
      client: {
        id: client?.contact?.data?.id || 0,
        name: client?.contact?.data?.name + '',
        phoneNumber: client?.contact?.data?.phoneNumber + '',
        country: {
          id: client?.contact?.data?.country?.id || 0,
          flag: client?.contact?.data?.country?.flag + '',
        },
        profilePicture: {
          id: client?.contact?.data?.profilePicture?.id || 0,
          url: client?.contact?.data?.profilePicture?.url ?? client?.contact?.data?.country?.flag,
        },
      },
    });

    cache.modify({
      fields: {
        messageConversationsV2(existingTodos = { data: [] }, { storeFieldName }) {
          // explicitly check the channelId and clientId in storeFieldName
          if (!storeFieldName.includes(`channelId":${channelId}`) || !storeFieldName.includes(`clientId":${clientId}`))
            return existingTodos;

          const cacheReference = cache.writeFragment({
            data: surrogateMessage,
            fragment: NEW_MESSAGE_FRAGMENT,
          });
          cacheReferenceId = cacheReference?.__ref ?? '';

          return {
            ...existingTodos,
            data: [...existingTodos.data, cacheReference],
          };
        },
      },
    });

    return cacheReferenceId;
  } catch (error) {
    console.error(error);
  }
}

export function cacheMarkBlockUnblockMessage({
  channelId,
  clientId,
  isBlocked,
}: {
  channelId: string;
  clientId: string;
  isBlocked: boolean;
}) {
  try {
    const authMe = cache.readQuery<authMeQuery>({ query: AUTH_ME });
    const client = cache.readQuery({
      query: GET_CONTACT_BY_ID,
      variables: { contactId: Number(clientId) },
    }) as ContactByIdQuery;
    const contact = client?.contact?.data;

    const message = createSurrogateMessage({
      channelId,
      clientId,
      messageContent: '',
      numMedia: 0,
      agent: {
        id: authMe?.authMe?.data?.user?.id || 0,
        name: authMe?.authMe?.data?.user?.name + '',
        profilePicture: {
          id: authMe?.authMe?.data?.user?.profilePicture?.id ?? 0,
          url: authMe?.authMe?.data?.user?.profilePicture?.url ?? '',
        },
      },
      client: {
        id: contact?.id || 0,
        name: contact?.name + '',
        phoneNumber: contact?.phoneNumber + '',
        country: {
          id: contact?.country?.id || 0,
          flag: contact?.country?.flag + '',
        },
        profilePicture: {
          id: contact?.profilePicture?.id || 0,
          url: contact?.profilePicture?.url ?? contact?.country?.flag,
        },
      },
    });

    message.conversationType = null;
    message.markAsBlocked = isBlocked;

    cache.updateQuery(
      {
        query: CURSOR_MESSAGES,
        variables: {
          clientId: Number(clientId),
          channelId: Number(channelId),
        },
      },
      (cachedData) => ({
        ...cachedData,
        messageConversationsV2: {
          ...cachedData?.messageConversationsV2,
          data: [message, ...(cachedData?.messageConversationsV2?.data || [])],
        },
      })
    );
  } catch {
    console.error('There was some issue on cacheMarkBlockUnblockMessage');
  }
}

export function cacheReplaceSurrogateMessage(responseMessage: MessageConversation, cacheReference: string) {
  try {
    cache.batch({
      update(cache) {
        // read cached data
        const prevData = cache.readFragment({
          id: cacheReference,
          fragment: NEW_MESSAGE_FRAGMENT,
        }) as MessageConversation;

        // create new fragment with response message
        const newCacheRef = cache.writeFragment({
          data: {
            ...prevData,
            id: responseMessage?.id,
          },
          fragment: NEW_MESSAGE_FRAGMENT,
        });

        // replace generated reference with new reference
        cache.modify({
          fields: {
            messageConversationsV2(existingTodos = { data: [] }) {
              return {
                ...existingTodos,
                data: existingTodos?.data?.map((ref: Reference) => {
                  return ref?.__ref === cacheReference ? newCacheRef : ref;
                }),
              };
            },
          },
        });

        // remove old reference (eg: MessageConversation:-1) with generated
        cache.evict({ id: cacheReference });
      },
    });
  } catch {
    console.error('There was some issue on cacheReplaceSurrogateMessage');
  }
}

export function cacheUpdateErrorMessage(cacheReference: string) {
  try {
    cache.updateFragment(
      {
        id: cacheReference,
        fragment: NEW_MESSAGE_FRAGMENT,
      },
      (data) => ({
        ...data,
        conversationStatus: 'undelivered',
      })
    );
  } catch {
    console.error('There was some issue on cacheUpdateNetworkErrorMessage');
  }
}

export function cacheUpdateRecentMessage({
  messageContent,
  clientId,
  channelId,
  numMedia,
}: {
  messageContent: string;
  clientId: string;
  channelId: string;
  numMedia: number;
}) {
  const FRAGMENT_RECENT_MESSAGE = gql`
    fragment UpdateRecentMessage on RecentMessageConversation {
      updatedAt
      unreadCounter
      messageConversation {
        conversationStatus
        conversationType
        direction
        messageContent
        numMedia
        pinned
        voicemailRecordingUrl
        forwardedTo {
          externalNumber
        }
      }
      draft {
        mediaCount
        messageContent
      }
    }
  `;

  try {
    cache.writeFragment({
      id: `RecentMessageConversation:{"client":{"id":${clientId}},"channel":{"id":${channelId}}}`,
      fragment: FRAGMENT_RECENT_MESSAGE,
      data: createRecentMessageOnSendMessage(messageContent, numMedia),
    });
  } catch {
    console.error('There was some issue on cacheUpdateRecentMessage');
  }
}

export function cachePinUnpinMessage(conversationId: number, pinned: boolean) {
  try {
    cache.modify({
      id: 'MessageConversation:' + conversationId,
      fields: {
        pinned() {
          return pinned;
        },
      },
    });
  } catch {
    console.error('There was some issue on cachePinUnpinMessage');
  }
}

export function cacheSetDefaultChannel(channelId: number, previousDefaultChannelId?: number | null) {
  try {
    cache.modify({
      id: 'ChannelUserWorkspace:' + channelId,
      fields: {
        isDefault() {
          return true;
        },
      },
    });

    if (!previousDefaultChannelId) return;
    cache.modify({
      id: 'ChannelUserWorkspace:' + previousDefaultChannelId,
      fields: {
        isDefault() {
          return false;
        },
      },
    });
  } catch (err) {
    console.error('Error updating cache in cacheSetDefaultChannel:', err);
  }
}

export function cacheSetDefaultCountry(workspaceId: number, country: Country) {
  try {
    cache.modify({
      id: 'Workspace:' + workspaceId,
      fields: {
        country() {
          return country;
        },
      },
    });
  } catch (err) {
    console.error('Error updating cache in cacheSetDefaultCountry:', err);
  }
}

export function cacheUpdateCallerId(updatedCallerId: { id: number; friendlyName: string }) {
  const CALLER_ID_FRAGMENT = gql`
    fragment callerId on CallerId {
      id
      friendlyName
    }
  `;

  try {
    cache.writeFragment({
      id: 'CallerId:' + updatedCallerId.id,
      fragment: CALLER_ID_FRAGMENT,
      data: updatedCallerId,
    });
  } catch {
    console.error('There was some issue on cacheUpdateCallerId');
  }
}

export function cacheAddNewCallerId(newCallerId: CallerId) {
  const CALLER_ID_FRAGMENT = gql`
    fragment callerId on CallerId {
      id
      friendlyName
      phoneNumber
      sid
      createdAt
      updatedAt
    }
  `;

  try {
    cache.modify({
      fields: {
        callerIdsV2(existingCallerIds = []) {
          const newCallerIdRef = cache.writeFragment({
            data: newCallerId,
            fragment: CALLER_ID_FRAGMENT,
          });

          return { ...existingCallerIds, data: [...existingCallerIds.data, newCallerIdRef] };
        },
      },
    });
  } catch {
    console.error('There was some issue on cacheAddNewCallerId');
  }
}

export function cacheDeleteCallerId(callerId: number) {
  try {
    const normalizedId = cache.identify({ id: callerId, __typename: 'CallerId' });
    cache.evict({ id: normalizedId });
    cache.gc();
  } catch {
    console.error('There was some issue on cacheDeleteCallerId');
  }
}

export function cacheUpdateWallet(loadWalletAmount: number | undefined) {
  try {
    const amount = (loadWalletAmount && loadWalletAmount) || 0;
    cache.updateQuery({ query: QUERY_GET_WALLET_DETAILS }, (data) => {
      const walletAmount = data?.getWalletDetailsV2?.data?.amount ?? 0;
      const updatedAmount = walletAmount + amount;
      return {
        ...data,
        getWalletDetailsV2: {
          ...data?.getWalletDetailsV2,
          data: {
            amount: updatedAmount,
          },
        },
      };
    });
  } catch (err) {
    console.error('Error updating cache in cacheUpdateLowBalanceAlert:', err);
  }
}

export function cacheUpdateLowBalanceAlert(isEmail: boolean, balanceThreshold: number) {
  try {
    cache.updateQuery<GetLowBalanceAlertQuery>({ query: QUERY_GET_LOW_BALANCE_ALERT }, (data) => {
      return {
        ...data,
        getLowBalanceAlert: {
          ...data?.getLowBalanceAlert,
          data: {
            ...data?.getLowBalanceAlert?.data,
            isEmail,
            balanceThreshold,
          },
        },
      };
    });
  } catch (err) {
    console.error('Error updating cache in cacheUpdateLowBalanceAlert:', err);
  }
}

export function cacheUpdateAutoRechargeConfig(
  enabled: boolean,
  credit: number,
  creditThreshold: number,
  targetBalance: number,
  isDisableAllowed: boolean
) {
  try {
    cache.updateQuery<GetAutoRechargeConfigsQuery>({ query: QUERY_GET_AUTO_RECHARGE_CONFIG }, (data) => {
      return {
        ...data,
        getAutoRechargeConfigs: {
          ...data?.getAutoRechargeConfigs,
          data: {
            ...data?.getAutoRechargeConfigs?.data,
            enabled,
            credit,
            creditThreshold,
            targetBalance,
            isDisableAllowed,
          },
        },
      };
    });
  } catch (err) {
    console.error('Error updating cache in cacheUpdateAutoRechargeConfig:', err);
  }
}

export function cacheAddContactNoteV2(clientId: number | null, node: ContactNoteV2) {
  try {
    cache.updateQuery(
      {
        query: CURSOR_CONTACT_NOTES,
        variables: {
          contactId: clientId,
          direction: null,
          cursor: null,
          limit: 10,
          order: null,
          sort: null,
        },
      },
      (cachedData) => {
        if (!cachedData?.contactNotesV2) return cachedData;

        return {
          ...cachedData,
          contactNotesV2: {
            ...cachedData.contactNotesV2,
            data: [node, ...(cachedData.contactNotesV2.data ?? [])],
            cursor: cachedData.contactNotesV2.cursor,
            message: cachedData.contactNotesV2.message,
          },
        };
      }
    );
  } catch (e) {
    console.error('There was some issue on cacheAddContactNoteV2', e);
  }
}

export function cacheAddContactV2(contact: Contact | undefined | null) {
  try {
    cache.modify({
      fields: {
        contactsV2(existingContacts = []) {
          return { ...existingContacts, data: [contact, ...existingContacts.data] };
        },
        //Having this function makes the count increase even if the numbers are same which should not happen as they are merged as one
        // {getContactsCount(existingCount) {
        //   if (!existingCount) return existingCount;

        //   return {
        //     ...existingCount,
        //     count: (existingCount.count ?? 0) + 1,
        //   };
        // },}
      },
    });
  } catch (error) {
    console.error('There was some issue updating cache after adding new contact:', error);
  }
}

export function cacheDeleteContactV2(contactId: number) {
  try {
    const normalizedId = cache.identify({ id: contactId, __typename: 'Contact' });
    cache.evict({ id: normalizedId });
    cache.gc();
  } catch (error) {
    console.error('There was some issue updating cache after contact deletion:', error);
  }
}

// export function cacheDeleteBulkCampaign(campaignId: number) {
//   try {
//     cache.modify({
//       fields: {
//         getBulkMessagingCampaignListCount: () => {},
//       },
//     });

//     const normalizedId = cache.identify({ id: campaignId, __typename: 'BulkMessagingCampaign' });
//     cache.evict({ id: normalizedId });
//     cache.gc();
//   } catch (error) {
//     console.error('There was some issue updating cache after campaign deletion:', error);
//   }
// }

export function cacheBlockContactV2(contactId: number, blockReason: string, blockedBy: string) {
  const CONTACT_FRAGMENT = gql`
    fragment contactParts on Contact {
      blockedBy {
        name
      }
      blockReason
      blockedOn
      isBlocked
      isPublished
    }
  `;

  try {
    const normalizedId = cache.identify({ id: contactId, __typename: 'Contact' });

    cache.writeFragment({
      fragment: CONTACT_FRAGMENT,
      id: normalizedId,
      data: {
        blockedBy: {
          name: blockedBy,
        },
        blockReason,
        blockedOn: new Date().toISOString(),
        isBlocked: true,
        isPublished: true,
      },
    });

    cache.modify({
      fields: {
        getBlockedContactsV2(existingBlockedContacts) {
          const blockedContacts = existingBlockedContacts?.data ?? [];
          return {
            ...existingBlockedContacts,
            data: [{ __ref: normalizedId }, ...blockedContacts],
          };
        },
        contactsV2(existingContacts) {
          const contactsData = existingContacts?.data || [];
          return {
            ...existingContacts,
            data: contactsData.filter((contact: { __ref: string }) => contact.__ref !== normalizedId),
          };
        },
      },
    });
  } catch (error) {
    console.error('There was some issue updating cache after contact block:', error);
  }
}

export function cachePinnedMessageCount({
  channelId,
  clientId,
  isPinned,
}: {
  channelId: string;
  clientId: string;
  isPinned: boolean;
}) {
  try {
    cache.updateQuery<CountPinnedMessageConversationsQuery>(
      {
        query: QUERY_PIN_MESSAGE_COUNT,
        variables: {
          channelId: +channelId,
          clientId: +clientId,
        },
      },
      (data) => {
        const currentCount = data?.countPinnedMessageConversations?.count ?? 0;
        return {
          ...data,
          countPinnedMessageConversations: {
            ...data?.countPinnedMessageConversations,
            count: isPinned ? currentCount + 1 : currentCount - 1,
          },
        };
      }
    );
  } catch (error) {
    console.error('There was some issue updating cachePinnedMessageCount', error);
  }
}

export function cacheAddSurrogateMessageMember({
  roomId,
  messageContent = '',
  authMe,
  currentUserWorkspaceId,
}: {
  roomId: number;
  messageContent: string;
  authMe: authMeQuery['authMe'];
  currentUserWorkspaceId: number;
}) {
  const QUERY = {
    query: CURSOR_MEMBER_MESSAGES,
    variables: {
      roomId,
    },
  };

  try {
    const data = cache.readQuery<GetChatMessagesV2Query>(QUERY);

    const prevMessages = data?.getChatMessagesV2?.data ?? [];
    const surrogateMsgCount = prevMessages?.filter((message) => String(message?.id)?.includes('666'))?.length ?? 0;

    const surrogateMessage = {
      id: surrogateMsgCount + '-' + 666,
      body: messageContent,
      createdAt: new Date().toISOString(),
      isPinned: false,
      type: 'message',
      messageConversation: {
        id: null,
        conversationStatus: null,
        effectiveStartedAt: null,
        effectiveEndedAt: null,
      },
      sender: {
        id: currentUserWorkspaceId,
        status: null,
        userOnlineManagement: {
          isActive: true,
          lastSeenAt: new Date().toISOString(),
          activeStatus: 'active',
          awayTill: null,
          message: null,
          awaySet: null,
          icon: null,
          __typename: 'UserOnlineManagement',
        },
        user: {
          id: authMe?.data?.user?.id,
          name: authMe?.data?.user?.name,
          email: authMe?.data?.user?.email,
          phoneNumber: authMe?.data?.user?.phoneNumber,
          profilePicture: {
            id: authMe?.data?.user?.profilePicture?.id,
            url: authMe?.data?.user?.profilePicture?.url,
            __typename: 'Media',
          },
          address: null,
          dueIn: null,
          accountStatus: null,
          timezone: null,
          averageRiskScore: null,
          totalMonthlyFee: null,
          lastPasswordResetAt: null,
          __typename: 'User',
        },
        __typename: 'UserWorkspace',
      },
      __typename: 'ChatMessage',
    };

    cache.updateQuery({ query: CURSOR_MEMBER_MESSAGES, variables: { roomId } }, (data) => {
      const previousData = data?.getChatMessagesV2?.data ? [...data.getChatMessagesV2.data] : [];

      return {
        ...data,
        getChatMessagesV2: {
          ...data?.getChatMessagesV2,
          data: [...previousData, surrogateMessage],
        },
      };
    });
  } catch (error) {
    console.error(error);
  }
}

export function cacheReplaceSurrogateMessageMember(roomId: number, responseMessage: ChatMessage) {
  const QUERY = {
    query: CURSOR_MEMBER_MESSAGES,
    variables: {
      roomId,
    },
  };

  try {
    cache.updateQuery<GetChatMessagesV2Query>(QUERY, (data) => {
      // helps prevent replace multiple surrogate message with same content, with same original message
      let isAttached = false;
      function isSurrogateMessageToReplace(message: ChatMessage) {
        return String(message?.id)?.includes('666') && responseMessage?.body === message?.body;
      }

      return {
        ...data,
        getChatMessagesV2: {
          ...data?.getChatMessagesV2,
          data: data?.getChatMessagesV2?.data?.map((message) => {
            if (!isAttached && isSurrogateMessageToReplace(message as ChatMessage)) {
              isAttached = true;
              // delete old
              const normalizedId = cache.identify({ id: message?.id, __typename: 'ChatMessage' });
              cache.evict({ id: normalizedId });
              cache.gc();
              return responseMessage;
            } else {
              return message;
            }
          }),
        },
      } as GetChatMessagesV2Query;
    });
  } catch (error) {
    console.error('There was some issue on cacheReplaceSurrogateMessageMember', error);
  }
}

export function cachePinUnpinMemberMessage(conversationId: number, pinned: boolean) {
  try {
    cache.modify({
      id: 'ChatMessage:' + conversationId,
      fields: {
        pinned() {
          return pinned;
        },
      },
    });
  } catch {
    console.error('There was some issue on cachePinUnpinMessage');
  }
}

export function cacheMemberPinnedMessageCount({ roomId, isPinned }: { roomId: number; isPinned: boolean }) {
  try {
    cache.updateQuery<CountPinnedChatMessagesQuery>(
      {
        query: QUERY_MEMBER_PIN_MESSAGE_COUNT,
        variables: {
          roomId: roomId,
        },
      },
      (data) => {
        const currentCount = data?.countPinnedChatMessages?.count ?? 0;
        return {
          ...data,
          countPinnedChatMessages: {
            ...data?.countPinnedChatMessages,
            count: isPinned ? currentCount + 1 : currentCount - 1,
          },
        };
      }
    );
  } catch (error) {
    console.error('There was some issue updating cachePinnedMessageCount', error);
  }
}

export function cacheUpdateInvoiceStatus(invoiceId: number, status: InvoiceStatusEnum) {
  try {
    cache.modify({
      id: 'Invoice:' + invoiceId,
      fields: {
        status() {
          return status;
        },
      },
    });
  } catch (err) {
    console.error('Error updating cache in cacheUpdateInvoiceStatus:', err);
  }
}

export function cacheUpdateMemberChannels(userWorkspaceId: number, channelCount: number, channelId: number) {
  try {
    cache.modify({
      id: 'WorkspaceMembers:' + userWorkspaceId,
      fields: {
        metrics() {
          return [
            {
              key: 'channel',
              value: channelCount,
            },
          ];
        },
        channels() {
          if (!channelId) return [];
          return [{ __ref: 'Channel:' + channelId }];
        },
      },
    });
  } catch (err) {
    console.error('Error updating cache in cacheUpdateMemberChannels:', err);
  }
}

export function cacheUpdateRecentMessageUpdatedAt(clientId: string, channelId: string) {
  const FRAGMENT = gql`
    fragment UpdatedAtRecentMessage on RecentMessageConversation {
      updatedAt
    }
  `;
  try {
    const id = cache.identify({
      __typename: 'RecentMessageConversation',
      client: { id: clientId },
      channel: { id: channelId },
    });

    cache.writeFragment({
      id,
      fragment: FRAGMENT,
      data: { updatedAt: new Date().toISOString() },
    });
  } catch {
    console.error('There was some issue on cacheUpdateRecentMessageUpdatedAt');
  }
}

export function cacheUpdateBlockUnBlockContact(contactId: number, isBlocked: boolean) {
  try {
    cache.updateQuery(
      {
        query: GET_CONTACT_BY_ID,
        variables: { contactId },
      },
      (data) => {
        return {
          contact: {
            ...data?.contact,
            data: {
              ...data?.contact?.data,
              isBlocked: isBlocked,
            },
          },
        };
      }
    );
  } catch (err) {
    console.error('Error updating cache in cacheUpdateMemberChannels:', err);
  }
}

export function decreaseTotalNumberCountInCache() {
  cache.evict({ fieldName: 'countChannels' });
  cache.evict({ fieldName: 'getAssignedChannelsCount' });
  cache.gc();
}

export function removeLiveConference(conferenceId: number) {
  const normalizedId = cache.identify({ id: conferenceId, __typename: 'LiveConferenceV2Data' });
  cache.evict({ id: normalizedId });
  cache.gc();
}

export function cacheUpdatePreferredContactMethodReminderAt(reminderAt: string) {
  try {
    const currentAuth = cache.readQuery<workspaceAuthMeV2Query>({ query: workspaceAuthMeV2Document });
    if (!currentAuth?.workspaceAuthMeV2?.data) return;

    cache.writeQuery<workspaceAuthMeV2Query>({
      query: workspaceAuthMeV2Document,
      data: {
        workspaceAuthMeV2: {
          ...currentAuth.workspaceAuthMeV2,
          data: {
            ...currentAuth.workspaceAuthMeV2.data,
            preferredContactMethodReminderAt: reminderAt,
          },
        },
      },
    });
  } catch (err) {
    console.error('There was some issue on cacheUpdatePreferredContactMethodReminderAt', err);
  }
}

export function cacheCancelWorkspaceDowngrade(cache: ApolloCache<unknown>, subscriptionId: string) {
  try {
    const cacheId = cache.identify({
      __typename: 'PlanSubscription',
      id: subscriptionId,
    });

    if (!cacheId) return;

    cache.modify({
      id: cacheId,
      fields: {
        hasScheduledChanges() {
          return false;
        },
      },
    });
  } catch (error) {
    console.error('Error cancelling workspace downgrade in cache:', error);
  }
}

export function cacheUpdateAiReports() {
  try {
    cache.evict({ fieldName: 'aiDashboardReportAnalytics' });
    cache.evict({ fieldName: 'aiDashboardCount' });
    cache.gc();
  } catch (error) {
    console.error('Error evicting AI dashboard cache:', error);
  }
}

const CHANNEL_RELEASE_FRAGMENT = gql`
  fragment ChannelForRelease on Channel {
    id
    friendlyName
    phoneNumber
    numberType
    country {
      id
      flag
    }
  }
`;

const CHANNEL_RELEASED_UPDATE_FRAGMENT = gql`
  fragment ChannelReleasedUpdate on Channel {
    releasedAt
    removalSchedule {
      scheduleAt
    }
  }
`;

export function cacheRemoveChannelsFromActiveList(channelIds: number[]) {
  try {
    const refs = new Set(channelIds.map((id) => cache.identify({ id, __typename: 'Channel' })));
    const assignRefs = new Set(channelIds.map((id) => `ChannelUserWorkspace:${id}`));

    cache.modify({
      fields: {
        channelsV2(existing = { data: [] }) {
          return { ...existing, data: existing.data?.filter((ref: Reference) => !refs.has(ref?.__ref)) ?? [] };
        },
        assignChannelsV2(existing = { data: [] }) {
          return { ...existing, data: existing.data?.filter((ref: Reference) => !assignRefs.has(ref?.__ref)) ?? [] };
        },
      },
    });
  } catch (error) {
    console.error('Error updating cache in cacheRemoveChannelsFromActiveList:', error);
  }
}

export function cacheInsertReleasedChannels(channelIds: number[]) {
  try {
    const releasedAt = new Date().toISOString();

    channelIds.forEach((channelId) => {
      const channelRef = cache.identify({ id: channelId, __typename: 'Channel' });
      if (!channelRef) return;

      const channel = cache.readFragment({ id: channelRef, fragment: CHANNEL_RELEASE_FRAGMENT }) as Pick<
        Channel,
        'id' | 'friendlyName' | 'phoneNumber' | 'numberType' | 'country'
      > | null;
      if (!channel) return;

      cache.writeFragment({
        id: channelRef,
        fragment: CHANNEL_RELEASED_UPDATE_FRAGMENT,
        data: { releasedAt, removalSchedule: null },
      });

      cache.modify({
        fields: {
          releasedAndScheduledChannels(existing = { data: [] }) {
            const alreadyPresent = existing.data?.some((ref: Reference) => ref?.__ref === channelRef);
            if (alreadyPresent) return existing;
            return { ...existing, data: [{ __ref: channelRef }, ...(existing.data ?? [])] };
          },
        },
      });
    });
  } catch (error) {
    console.error('Error updating cache in cacheInsertReleasedChannels:', error);
  }
}

export function cacheMoveChannelBackToActive(channelId: number) {
  try {
    const channelRef = cache.identify({ id: channelId, __typename: 'Channel' });
    if (!channelRef) return;

    cache.modify({
      id: channelRef,
      fields: {
        removalSchedule: (existing) => (existing ? { ...existing, scheduleAt: null } : existing),
      },
    });

    cache.modify({
      fields: {
        releasedAndScheduledChannels(existing = { data: [] }) {
          return { ...existing, data: existing.data?.filter((ref: Reference) => ref?.__ref !== channelRef) ?? [] };
        },
        channelsV2(existing = { data: [] }) {
          const alreadyPresent = existing.data?.some((ref: Reference) => ref?.__ref === channelRef);
          if (alreadyPresent) return existing;
          return { ...existing, data: [{ __ref: channelRef }, ...(existing.data ?? [])] };
        },
      },
    });
  } catch (error) {
    console.error('Error updating cache in cacheMoveChannelBackToActive:', error);
  }
}

export function cacheBumpAssignedChannelsCount(delta: number) {
  try {
    cache.modify({
      fields: {
        getAssignedChannelsCount(existing) {
          if (!existing) return existing;
          return {
            ...existing,
            count: Math.max(0, (existing.count ?? 0) + delta),
          };
        },
      },
    });
  } catch (error) {
    console.error('Error updating cache in cacheBumpAssignedChannelsCount:', error);
  }
}

export function cacheBumpPhoneNumberStatusCount(delta: {
  activeCount?: number;
  releasedAndScheduledChannelsCount?: number;
}) {
  try {
    cache.modify({
      fields: {
        phoneNumberStatusCount(existing) {
          if (!existing?.data) return existing;
          return {
            ...existing,
            data: {
              ...existing.data,
              activeCount: Math.max(0, (existing.data.activeCount ?? 0) + (delta.activeCount ?? 0)),
              releasedAndScheduledChannelsCount: Math.max(
                0,
                (existing.data.releasedAndScheduledChannelsCount ?? 0) + (delta.releasedAndScheduledChannelsCount ?? 0)
              ),
            },
          };
        },
      },
    });
  } catch (error) {
    console.error('Error updating cache in cacheBumpPhoneNumberStatusCount:', error);
  }
}
